import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/shared/auth/authStore";
import { useAsyncFeedbackStore } from "@/shared/feedback/asyncFeedbackStore";

let refreshInFlight: Promise<void> | null = null;

function requestUrlForMatch(config: InternalAxiosRequestConfig | undefined): string {
  if (!config) return "";
  const raw = config.url || "";
  try {
    if (/^https?:\/\//i.test(raw)) return raw;
    return String(config.baseURL || "") + raw;
  } catch {
    return String(config.baseURL || "") + String(config.url || "");
  }
}

function isAuthRecoverableUrl(config: InternalAxiosRequestConfig): boolean {
  const lower = requestUrlForMatch(config).toLowerCase();
  if (lower.includes("/authenticate")) return false;
  if (lower.includes("/refreshtoken")) return false;
  if (lower.includes("/generate") && lower.includes("recover")) return false;
  if (lower.includes("/validate") && lower.includes("recover")) return false;
  if (lower.includes("/change") && lower.includes("recover")) return false;
  return true;
}

function runRefresh(): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  const token =
    useAuthStore.getState().token ?? localStorage.getItem("token");
  if (!token) return Promise.reject(new Error("no token"));
  refreshInFlight = useAuthStore
    .getState()
    .refreshToken(token)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export const http = axios.create({
  headers: { Accept: "application/json" },
});

function trackRequest(config: InternalAxiosRequestConfig) {
  if (config.headers?.["X-Skip-Global-Loading"]) return;
  useAsyncFeedbackStore.getState().beginRequest();
}

function untrackRequest(config: InternalAxiosRequestConfig | undefined) {
  if (!config || config.headers?.["X-Skip-Global-Loading"]) return;
  useAsyncFeedbackStore.getState().endRequest();
}

http.interceptors.request.use((config) => {
  trackRequest(config);
  const token = useAuthStore.getState().token;
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

http.interceptors.response.use(
  (response) => {
    untrackRequest(response.config);
    return response;
  },
  async (error: AxiosError) => {
    untrackRequest(error.config);
    const status = error.response?.status;
    const config = error.config as InternalAxiosRequestConfig & { _retry401?: boolean };

    if (status !== 401 || !config || config._retry401) {
      return Promise.reject(error);
    }
    if (!isAuthRecoverableUrl(config)) {
      return Promise.reject(error);
    }

    config._retry401 = true;
    try {
      await runRefresh();
      if (!useAuthStore.getState().isAuthenticated()) {
        useAuthStore.getState().logout();
        return Promise.reject(error);
      }
      const newTok = useAuthStore.getState().token;
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${newTok}`;
      return http.request(config);
    } catch {
      return Promise.reject(error);
    }
  },
);

export async function ensureFreshToken(): Promise<string | null> {
  if (!useAuthStore.getState().isAuthenticated()) {
    return null;
  }
  const t = useAuthStore.getState().token;
  if (!t) return null;
  try {
    await useAuthStore.getState().refreshToken(t);
  } catch {
    return null;
  }
  return useAuthStore.getState().token;
}
